import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { useParams, useNavigate } from "react-router-dom";
import {
  Sparkles, AlignLeft, LayoutGrid, Image as ImageIcon, Users, Euro,
  UserCheck, Shield, Calendar, Star, HelpCircle, MessageSquare,
  CheckCircle2, Minus, Plus, Trash2, GripVertical, Eye, EyeOff,
  Save, RotateCcw, Loader2, Upload, ArrowLeft, ArrowRight, X,
} from "lucide-react";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import api from "@/lib/api";
import { BLOCK_META, createEmptyBlock } from "@/lib/proposalBlocks";
import { BlockRenderer } from "@/components/proposalBlocks/BlockRenderer";
import { renderContent, PREVIEW_CONTEXT } from "@/lib/proposalPlaceholders";

const ICON_MAP = {
  Sparkles, AlignLeft, LayoutGrid, Image: ImageIcon, Users, Euro,
  UserCheck, Shield, Calendar, Star, HelpCircle, MessageSquare,
  CheckCircle2, Minus,
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Fake lead data used by the live preview so data-driven blocks render.
const PREVIEW_LEAD_DATA = {
  qualification: {
    contact: { name: "Ana Silva" },
    cared_person: { name: "Manuel Silva", age: 82, sex: "M" },
    cared_person_2: { name: "Rosa Silva", age: 79, sex: "F" },
    has_second_beneficiary: true,
  },
  proposal: {
    caring_advantages: [
      "Equipa dedicada com formação contínua em geriatria e apoio domiciliário.",
      "Supervisão técnica quinzenal por enfermeiro coordenador.",
      "Registo diário de cuidados partilhado com a família.",
      "Substituições planeadas — procedemos de imediato à procura de um substituto qualificado sempre que necessário.",
    ],
    timeline: [
      { phase: "Aceitação da proposta", description: "Formalizamos o contrato e agendamos a visita inicial." },
      { phase: "Preparação da equipa", description: "Selecionamos a Ajudante Familiar mais adequada." },
      { phase: "Visita técnica", description: "Enfermeiro coordenador valida o plano no domicílio." },
      { phase: "Início do serviço", description: "Começa o apoio dedicado à sua família." },
    ],
    faq: [
      { question: "Posso cancelar o serviço a qualquer momento?", answer: "Sim. O aviso prévio é de 15 dias e não há qualquer penalização." },
      { question: "A caução é devolvida?", answer: "Integralmente, no final do contrato, exceto por serviços prestados e não pagos." },
    ],
    monthly_price_min: 1200,
    hourly_rate: 12,
    options: [
      { id: "opt1", name: "SEMANA", is_primary: true, mode: "continuidade", days_per_week: 5, hours_per_day: 6, hourly_price: 12, weekly_value: 360, monthly_avg: 1559.88 },
      { id: "opt2", name: "FDS", is_primary: false, mode: "continuidade", days_per_week: 2, hours_per_day: 8, hourly_price: 14, weekly_value: 224, monthly_avg: 970.72 },
    ],
    qt_confirmed_by_name: "Beatriz Costa",
    qt_adjusted: false,
    additional_notes: "",
    commercial_terms: "Serviço contratado por período mínimo de 3 meses.",
    validity_days: 30,
  },
  deposit: { total_amount: 900, kickstart_amount: 500, remainder_amount: 400, service_start_date: "2026-03-01" },
  testimonials_url: "https://caring.pt/testemunhos",
  manager: {
    name: "Beatriz Costa",
    title: "Gestora de Cliente Sénior",
    email: "beatriz.costa@caring.pt",
    phone: "800 450 191",
    bio: "Enfermeira especialista com 15 anos de experiência em apoio domiciliário.",
    photo_url: null,
  },
  acceptance: null,
};

// ============ SORTABLE BLOCK ROW ============
function SortableBlockRow({ block, isSelected, onSelect, onDelete, onToggleVisible }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: block.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  const meta = BLOCK_META[block.type] || { label: block.type, icon: "AlignLeft" };
  const Icon = ICON_MAP[meta.icon] || AlignLeft;
  return (
    <div ref={setNodeRef} style={style}
      className={`group flex items-center gap-2 border rounded-lg p-3 mb-2 cursor-pointer transition-all
        ${isSelected ? "bg-primary border-primary text-primary-foreground" : "bg-card border-border hover:border-primary/40"}`}
      onClick={onSelect}
      data-testid={`block-row-${block.type}`}
    >
      <button {...attributes} {...listeners}
        className={`cursor-grab active:cursor-grabbing p-1 rounded ${isSelected ? "text-primary-foreground/70 hover:bg-card/10" : "text-muted-foreground hover:bg-background"}`}
        onClick={(e) => e.stopPropagation()}
        data-testid={`block-drag-${block.type}`}>
        <GripVertical className="w-4 h-4" />
      </button>
      <Icon className={`w-4 h-4 ${isSelected ? "text-[hsl(var(--warning))]" : "text-primary"}`} />
      <div className="flex-1 text-sm font-medium truncate">
        {meta.label}
        {meta.dataOnly && <span className={`ml-2 text-[9px] uppercase tracking-wider ${isSelected ? "text-primary-foreground/60" : "text-muted-foreground"}`}>auto</span>}
      </div>
      <button onClick={(e) => { e.stopPropagation(); onToggleVisible(); }}
        className={`p-1 rounded ${isSelected ? "text-primary-foreground/70 hover:bg-card/10" : "text-muted-foreground hover:bg-background"}`}
        title={block.visible ? "Ocultar bloco" : "Mostrar bloco"}
        data-testid={`block-toggle-${block.type}`}>
        {block.visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
      </button>
      <button onClick={(e) => { e.stopPropagation(); onDelete(); }}
        className={`p-1 rounded ${isSelected ? "text-primary-foreground/70 hover:bg-red-500/30" : "text-muted-foreground hover:text-[hsl(var(--danger))] hover:bg-[hsl(var(--danger-bg))]"}`}
        title="Remover bloco"
        data-testid={`block-delete-${block.type}`}>
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
}

// ============ FIELD EDITORS ============
function FieldEditor({ field, value, onChange, onImageUpload, uploading }) {
  const inp = "w-full bg-card border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary";
  if (field.type === "text") {
    return <input type="text" value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={inp} data-testid={`field-${field.key}`} />;
  }
  if (field.type === "textarea") {
    return <textarea rows={4} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={`${inp} min-h-[100px] resize-y`} data-testid={`field-${field.key}`} />;
  }
  if (field.type === "number") {
    return <input type="number" value={value ?? ""} onChange={(e) => onChange(Number(e.target.value) || 0)} className={inp} data-testid={`field-${field.key}`} />;
  }
  if (field.type === "boolean") {
    return (
      <label className="inline-flex items-center gap-2 text-sm">
        <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} data-testid={`field-${field.key}`} />
        <span className="text-muted-foreground">Ativo</span>
      </label>
    );
  }
  if (field.type === "select") {
    return (
      <select value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={inp} data-testid={`field-${field.key}`}>
        {(field.options || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    );
  }
  if (field.type === "image_upload") {
    return (
      <div className="space-y-2">
        {value && (
          <div className="relative inline-block">
            <img src={/^https?:/i.test(value) ? value : `${BACKEND_URL}${value}`} alt=""
              className="max-h-40 rounded border border-border" />
            <button onClick={() => onChange("")}
              className="absolute -top-2 -right-2 bg-card border border-border rounded-full p-1 shadow-sm hover:bg-[hsl(var(--danger-bg))]"
              data-testid={`field-${field.key}-clear`}>
              <X className="w-3 h-3 text-[hsl(var(--danger))]" />
            </button>
          </div>
        )}
        <label className="inline-flex items-center gap-2 text-xs cursor-pointer bg-primary hover:bg-primary text-primary-foreground rounded-lg px-3 py-2 disabled:opacity-60">
          {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
          {value ? "Substituir imagem" : "Carregar imagem"}
          <input type="file" accept="image/*" className="hidden"
            onChange={async (e) => {
              const f = e.target.files?.[0];
              if (!f) return;
              const url = await onImageUpload(f);
              if (url) onChange(url);
              e.target.value = "";
            }}
            data-testid={`field-${field.key}-upload`} />
        </label>
        <input type="text" value={value ?? ""} onChange={(e) => onChange(e.target.value)}
          placeholder="ou cole um URL de imagem"
          className={`${inp} text-xs`} />
      </div>
    );
  }
  if (field.type === "items_list") {
    const arr = Array.isArray(value) ? value : [];
    return (
      <div className="space-y-3">
        {arr.map((item, i) => (
          <div key={i} className="bg-card border border-border rounded-lg p-3 space-y-2 relative">
            <div className="flex items-center justify-between mb-1">
              <div className="text-xs text-muted-foreground font-semibold">ITEM {i + 1}</div>
              <div className="flex items-center gap-1">
                <button onClick={() => { const n = [...arr]; if (i > 0) { [n[i-1], n[i]] = [n[i], n[i-1]]; onChange(n); } }}
                  className="p-1 text-muted-foreground hover:text-primary" disabled={i === 0}>↑</button>
                <button onClick={() => { const n = [...arr]; if (i < n.length - 1) { [n[i+1], n[i]] = [n[i], n[i+1]]; onChange(n); } }}
                  className="p-1 text-muted-foreground hover:text-primary" disabled={i === arr.length - 1}>↓</button>
                <button onClick={() => onChange(arr.filter((_, j) => j !== i))}
                  className="p-1 text-red-500 hover:text-[hsl(var(--danger))]" data-testid={`item-remove-${i}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
            {(field.fields || []).map((f) => (
              <div key={f.key}>
                <label className="block text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold mb-1">{f.label}</label>
                {f.type === "textarea" ? (
                  <textarea rows={2} value={item[f.key] ?? ""} onChange={(e) => { const n = [...arr]; n[i] = { ...n[i], [f.key]: e.target.value }; onChange(n); }} className={inp} />
                ) : (
                  <input type="text" value={item[f.key] ?? ""} onChange={(e) => { const n = [...arr]; n[i] = { ...n[i], [f.key]: e.target.value }; onChange(n); }} className={inp} />
                )}
              </div>
            ))}
          </div>
        ))}
        <button onClick={() => onChange([...arr, {}])}
          className="text-xs text-primary hover:text-[hsl(var(--warning))] inline-flex items-center gap-1"
          data-testid="items-add">
          <Plus className="w-3.5 h-3.5" /> Adicionar item
        </button>
      </div>
    );
  }
  return null;
}

// ============ BLOCK EDITOR PANEL ============
function BlockEditor({ block, onChange, onUploadImage, uploading }) {
  const meta = BLOCK_META[block.type];
  if (!meta) return <div className="text-red-500 p-4">Tipo desconhecido</div>;
  const update = (key, val) => onChange({ ...block, content: { ...block.content, [key]: val } });
  const fields = meta.fields || [];
  return (
    <div className="space-y-4">
      <div className="border-b border-border pb-3 mb-4">
        <div className="text-[10px] uppercase tracking-[0.25em] text-[hsl(var(--warning))] font-semibold mb-1">Bloco</div>
        <div className="font-display text-xl text-primary">{meta.label}</div>
        <div className="text-xs text-muted-foreground mt-1">{meta.description}</div>
        {meta.dataOnly && (
          <div className="mt-2 text-[10px] uppercase tracking-wider text-[hsl(var(--warning))] font-semibold">Dados dinâmicos · pull automático do lead</div>
        )}
      </div>
      {fields.map((f) => {
        // Handle showIf conditional
        if (f.showIf) {
          const [k, v] = Object.entries(f.showIf)[0];
          if ((block.content || {})[k] !== v) return null;
        }
        return (
          <div key={f.key}>
            <label className="block text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold mb-1.5">{f.label}</label>
            <FieldEditor field={f} value={(block.content || {})[f.key]} onChange={(v) => update(f.key, v)} onImageUpload={onUploadImage} uploading={uploading} />
          </div>
        );
      })}
      <div className="pt-4 border-t border-border text-[10px] text-muted-foreground leading-relaxed">
        <b>Variáveis disponíveis:</b> <code>&#123;&#123;contact.name&#125;&#125;</code>, <code>&#123;&#123;contact.name_with_treatment&#125;&#125;</code> (inclui Sr./Sra./Dr.), <code>&#123;&#123;contact.treatment&#125;&#125;</code>, <code>&#123;&#123;contact.first_name&#125;&#125;</code>, <code>&#123;&#123;cared_person.name&#125;&#125;</code>, <code>&#123;&#123;cared_person_2.name&#125;&#125;</code>, <code>&#123;&#123;proposal.personal_message&#125;&#125;</code>, <code>&#123;&#123;proposal.executive_summary&#125;&#125;</code>, <code>&#123;&#123;proposal.commercial_terms&#125;&#125;</code>, <code>&#123;&#123;proposal.validity_days&#125;&#125;</code>, <code>&#123;&#123;gestor.name&#125;&#125;</code>.
      </div>
    </div>
  );
}

// ============ ADD BLOCK PALETTE ============
function AddBlockPalette({ onAdd }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button onClick={() => setOpen(!open)}
        className="w-full inline-flex items-center justify-center gap-2 bg-[hsl(var(--warning))] hover:bg-[hsl(var(--warning))] text-primary-foreground rounded-lg px-4 py-2.5 text-sm font-semibold"
        data-testid="add-block-btn">
        <Plus className="w-4 h-4" /> Adicionar bloco
      </button>
      {open && (
        <div className="absolute z-10 top-full mt-2 left-0 right-0 bg-card border border-border rounded-lg shadow-xl p-2 max-h-96 overflow-y-auto">
          {Object.entries(BLOCK_META).map(([type, meta]) => {
            const Icon = ICON_MAP[meta.icon] || AlignLeft;
            return (
              <button key={type} onClick={() => { onAdd(type); setOpen(false); }}
                className="w-full flex items-start gap-3 p-2.5 rounded-lg hover:bg-background text-left transition-colors"
                data-testid={`add-block-${type}`}>
                <Icon className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                <div>
                  <div className="text-sm font-semibold text-primary">{meta.label}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{meta.description}</div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============ MAIN PAGE ============
export default function ProposalTemplateSettings() {
  const { templateId } = useParams();
  const nav = useNavigate();
  const [templateMeta, setTemplateMeta] = useState({ id: "", name: "", description: "", is_default: false });
  const [blocks, setBlocks] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const [dirty, setDirty] = useState(false);
  const initialRef = useRef(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // If we have a templateId param, load that specific template; else fallback to default (legacy /proposal-template)
      const url = templateId ? `/proposal-templates/${templateId}` : "/proposal-template";
      const { data } = await api.get(url);
      setBlocks(data.blocks || []);
      setTemplateMeta({
        id: data.id || "",
        name: data.name || "Standard",
        description: data.description || "",
        is_default: !!data.is_default,
      });
      initialRef.current = JSON.stringify(data.blocks || []);
      setDirty(false);
      if ((data.blocks || []).length > 0) {
        setSelectedId((prev) => prev || data.blocks[0].id);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao carregar template");
    } finally { setLoading(false); }
  }, [templateId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (initialRef.current !== null) {
      setDirty(JSON.stringify(blocks) !== initialRef.current);
    }
  }, [blocks]);

  const onDragEnd = (evt) => {
    const { active, over } = evt;
    if (!over || active.id === over.id) return;
    const oldIdx = blocks.findIndex((b) => b.id === active.id);
    const newIdx = blocks.findIndex((b) => b.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    setBlocks(arrayMove(blocks, oldIdx, newIdx));
  };

  const addBlock = (type) => {
    const b = createEmptyBlock(type);
    setBlocks([...blocks, b]);
    setSelectedId(b.id);
    toast.success(`Adicionado: ${BLOCK_META[type]?.label || type}`);
  };

  const deleteBlock = (id) => {
    if (!window.confirm("Remover este bloco?")) return;
    setBlocks(blocks.filter((b) => b.id !== id));
    if (selectedId === id) setSelectedId(null);
  };

  const toggleVisible = (id) => {
    setBlocks(blocks.map((b) => b.id === id ? { ...b, visible: !b.visible } : b));
  };

  const updateSelected = (updated) => {
    setBlocks(blocks.map((b) => b.id === updated.id ? updated : b));
  };

  const save = async () => {
    setSaving(true);
    try {
      const url = templateId ? `/proposal-templates/${templateId}` : "/proposal-template";
      const payload = {
        id: templateMeta.id || "singleton",
        name: templateMeta.name,
        description: templateMeta.description,
        is_default: templateMeta.is_default,
        blocks,
      };
      const { data } = await api.put(url, payload);
      initialRef.current = JSON.stringify(data.blocks || []);
      setDirty(false);
      toast.success("Template guardado");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao guardar");
    } finally { setSaving(false); }
  };

  const resetToDefault = async () => {
    if (!templateMeta.is_default && templateId) {
      // Non-default template — reload from server without wiping
      await load();
      return;
    }
    if (!window.confirm("Repor o template para o layout inicial? As alterações atuais serão perdidas.")) return;
    try {
      const { data } = await api.post("/proposal-template/reset-default");
      setBlocks(data.blocks || []);
      initialRef.current = JSON.stringify(data.blocks || []);
      setDirty(false);
      toast.success("Template reposto");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao repor");
    }
  };

  const uploadImage = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/proposal-template/assets", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Imagem carregada");
      return data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao carregar imagem");
      return null;
    } finally { setUploading(false); }
  };

  const selected = useMemo(() => blocks.find((b) => b.id === selectedId), [blocks, selectedId]);

  // Rendered blocks for preview — expand placeholders client-side using sample data
  const previewBlocks = useMemo(() => {
    return (blocks || [])
      .filter((b) => b.visible !== false)
      .map((b) => ({ ...b, content: renderContent(b.content || {}, PREVIEW_CONTEXT) }));
  }, [blocks]);

  const previewForms = {
    observations: { obsText: "", obsName: "", sending: false, submitted: false, setObsText: () => {}, setObsName: () => {}, onSubmit: () => {}, onReset: () => {} },
    acceptance: { name: "", accepting: false, setName: () => {}, onAccept: () => {}, onGoContract: () => {} },
  };

  if (loading) {
    return <div className="p-12 text-center text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> A carregar template...</div>;
  }

  return (
    <div className="min-h-screen bg-muted/40">
      <div className="border-b border-border bg-card/60 backdrop-blur sticky top-0 z-30">
        <div className="max-w-[1800px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {templateId && (
              <button onClick={() => nav("/settings/proposal-templates")}
                className="p-2 rounded-lg text-muted-foreground hover:bg-background"
                data-testid="back-to-list">
                <ArrowLeft className="w-4 h-4" />
              </button>
            )}
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[hsl(var(--warning))] font-semibold flex items-center gap-2">
                {templateMeta.is_default && <span className="bg-[hsl(var(--warning))] text-primary-foreground px-1.5 py-0.5 rounded-sm text-[9px]">DEFAULT</span>}
                Editar template
              </div>
              {templateId ? (
                <input
                  value={templateMeta.name}
                  onChange={(e) => { setTemplateMeta({ ...templateMeta, name: e.target.value }); setDirty(true); }}
                  className="font-display text-2xl text-primary bg-transparent border-0 border-b-2 border-transparent focus:border-[hsl(var(--warning))] focus:outline-none min-w-[300px]"
                  data-testid="template-name-input"
                />
              ) : (
                <h1 className="font-display text-2xl text-primary">{templateMeta.name || "Construtor de Proposta"}</h1>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowPreview(!showPreview)}
              className="text-xs px-3 py-2 rounded-lg border border-border text-muted-foreground hover:text-primary inline-flex items-center gap-1.5"
              data-testid="toggle-preview">
              {showPreview ? <><ArrowLeft className="w-3.5 h-3.5" /> Esconder preview</> : <><Eye className="w-3.5 h-3.5" /> Mostrar preview</>}
            </button>
            <button onClick={resetToDefault}
              className="text-xs px-3 py-2 rounded-lg border border-border text-muted-foreground hover:text-[hsl(var(--danger))] hover:border-[hsl(var(--danger))]/40 inline-flex items-center gap-1.5"
              data-testid="reset-btn">
              <RotateCcw className="w-3.5 h-3.5" /> {templateMeta.is_default ? "Repor default" : "Descartar alterações"}
            </button>
            <button onClick={save} disabled={saving || !dirty}
              className="text-sm px-5 py-2 rounded-lg bg-[hsl(var(--warning))] hover:bg-[hsl(var(--warning))] text-primary-foreground font-semibold inline-flex items-center gap-2 disabled:opacity-50"
              data-testid="save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {dirty ? "Guardar alterações" : "Sem alterações"}
            </button>
          </div>
        </div>
      </div>

      <div className={`max-w-[1800px] mx-auto px-6 py-6 grid gap-6 ${showPreview ? "grid-cols-1 lg:grid-cols-[300px_360px_1fr]" : "grid-cols-1 lg:grid-cols-[320px_1fr]"}`}>
        {/* Blocks list */}
        <div className="bg-card rounded-2xl border border-border p-4 h-[calc(100vh-140px)] overflow-y-auto sticky top-24" data-testid="blocks-list">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-3">
            {blocks.length} bloco{blocks.length !== 1 ? "s" : ""}
          </div>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={blocks.map((b) => b.id)} strategy={verticalListSortingStrategy}>
              {blocks.map((b) => (
                <SortableBlockRow key={b.id} block={b}
                  isSelected={selectedId === b.id}
                  onSelect={() => setSelectedId(b.id)}
                  onDelete={() => deleteBlock(b.id)}
                  onToggleVisible={() => toggleVisible(b.id)}
                />
              ))}
            </SortableContext>
          </DndContext>
          <div className="mt-3">
            <AddBlockPalette onAdd={addBlock} />
          </div>
        </div>

        {/* Block editor */}
        <div className="bg-card rounded-2xl border border-border p-6 h-[calc(100vh-140px)] overflow-y-auto sticky top-24" data-testid="block-editor">
          {selected ? (
            <BlockEditor block={selected} onChange={updateSelected} onUploadImage={uploadImage} uploading={uploading} />
          ) : (
            <div className="text-center text-muted-foreground py-12">
              <div className="text-sm">Selecione um bloco à esquerda ou adicione um novo para começar a editar.</div>
            </div>
          )}
        </div>

        {/* Preview */}
        {showPreview && (
          <div className="bg-background rounded-2xl border border-border overflow-hidden h-[calc(100vh-140px)] flex flex-col sticky top-24" data-testid="preview-panel">
            <div className="px-4 py-2 border-b border-border bg-card/50 flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">
                Preview · dados de exemplo
              </div>
              <div className="text-[10px] text-muted-foreground">{previewBlocks.length} bloco{previewBlocks.length !== 1 ? "s" : ""} visível{previewBlocks.length !== 1 ? "eis" : ""}</div>
            </div>
            <div className="overflow-y-auto flex-1 text-foreground" style={{ transform: "scale(0.62)", transformOrigin: "top left", width: "161%", height: "161%" }}>
              {previewBlocks.map((b) => (
                <BlockRenderer key={b.id} block={b} data={PREVIEW_LEAD_DATA} forms={previewForms} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
